import type {ILayerView} from "@esWebAPI/es/model/Layer";
import type {Feature, Geometry} from "geojson";
import type TerritoryModel from "@esWebAPI/es/map/TerritoryModel";
import type {TerritoryBaseFeatureWorker} from "@esWebAPI/es/map/layer/territory/TerritoryLayer";

export type ProgressStatus = {
    progress: number,
    time: number,
    message: string
};

export class TerritoryLayerWorkerBroker implements TerritoryBaseFeatureWorker {

    private readonly territoryModel: TerritoryModel;
    private worker: Worker|undefined;
    private visualCenterWorker: Worker|undefined;
    private sequenceNumber: number = 0;   // Sequence for messageIds
    private messagesCallbacks: any = {}; // Map with key as messageId and value as callback

    constructor(territoryModel: TerritoryModel) {
        this.territoryModel = territoryModel;
    }

    private getSequenceNumber() {
        return ++this.sequenceNumber;
    }

    private getAppUrl() {
        let appUrl = es.appUrl;
        if (es.isPublicAccess) {
            appUrl = es.publicUrl;
        }
        return appUrl;
    }

    public getWorker() {
        return this.worker;
    }

    private createWorker(title: string, options: any, progressListener?: (status: ProgressStatus) => void){

        this.worker = new Worker(this.getAppUrl() + "js/esApp/workers/territoryLayerWorker.js?version=" + es.ver,
            {name: "TLW_" + title});

        this.visualCenterWorker = new Worker(this.getAppUrl() + "js/esApp/workers/territoryVisualCenterWorker.js?version=" + es.ver,
            {name: `${"TLW_" + title}_territoryVisualCenterWorker`});

        this.worker.addEventListener("message", (message: any) => this.onMessage(message));
        this.worker.addEventListener("error", (error: any) => this.onError(error));


        this.visualCenterWorker.addEventListener("message", (message: any) => this.onMessage(message));
        this.visualCenterWorker.addEventListener("error", (error: any) => this.onError(error));

        if (progressListener) {
            options.isProgressListener = true;
            this.messagesCallbacks["loadProgress"] = progressListener;
        }
    }

    public create(layerView: ILayerView, options: any, progressListener?: (status: ProgressStatus) => void): Promise<void> {
        return new Promise((resolve, _reject) => {
            es.api.territory.hasPbf(layerView.id).then((hasPbf: boolean) => {

            let isUseFastLoading = true;
            const layerViewMapping = this.territoryModel.getRelatedLayerViewMapping();
            const layerMetaId =
                (layerViewMapping.baseLayerView[layerView.id]) ||
                (layerViewMapping.relatedLayerView[layerView.id]) ||
                (this.territoryModel.isRelatedExtraPointLayer(layerView.id) && layerViewMapping.extraRelatedPointLayerView[layerView.id]) || -1;

                if (layerMetaId === -1) {
                    throw new Error("Layer is not a territory layer " + layerView.id);
                }

                const lsLoadStatusKey = "es_ter_load_status_" + layerMetaId;
                const layerLoadStatus = es.util.localStorage.get(lsLoadStatusKey);
                if (layerLoadStatus === null) {
                    console.log("layerLoadStatus", layerMetaId, layerView.title, "first time loading");
                    es.util.localStorage.set(lsLoadStatusKey, "loading");
                } else if (layerLoadStatus === "success") {
                    // we've done this before. we should be fine
                    console.log("layerLoadStatus", layerMetaId, layerView.title, "previous success");
                } else if (layerLoadStatus === "loading") {
                    // we're in trouble. it looks like the last load failed
                    console.warn("layerLoadStatus", layerMetaId, layerView.title, "previous fail! fallback to legacy loading");
                    isUseFastLoading = false;
                    hasPbf = false;
                    es.util.TrackingEvents.push(['MT.LT.Previous_Fail_Detected_' + layerMetaId]);
                }

                const title = encodeURIComponent(layerView.title.replaceAll(" ", "_"));
                this.createWorker(title, options, progressListener);
                const format = hasPbf ? "pbf" : "geojson";
                const url = es.restUrl + "/territory/" + format + "/" + layerMetaId + "?version=" + es.ver;
                const message = {
                    cmd: "load",
                    data: {
                        url: url,
                        options: options,
                        isPbf: hasPbf,
                        id: layerMetaId,
                        isUseFastLoading: isUseFastLoading,
                        requestedColumns: layerView.styleSettings.labelSettings.labelAttrs.join()
                    }
                };
                this.sendMessage(message, () => {
                    if (isUseFastLoading) {
                        es.util.localStorage.set(lsLoadStatusKey, "success");
                    }
                    resolve();
                });
            });
        });
    }


    public createLocal(options: any, callback: any, progressListener: any){
        const title = "TerrOutlines";
        this.createWorker(title, options, progressListener);
        const message = {
            cmd: "loadLocal",
            data: {
                options: options
            }
        };
        this.sendMessage(message, callback);
    }

    public destroy() {
        this.worker?.terminate();
        this.worker = undefined;
        this.sequenceNumber = 0;
        this.messagesCallbacks = {};
        this.visualCenterWorker?.terminate();
    }

    private onMessage(message: any) {
        const callback = this.messagesCallbacks[message.data.id];
        if(!callback){
            console.error("No callback for message", message.data);
            return;
        }
        callback(message.data.data);

        if (!isNaN(message.data.id)) {
            delete this.messagesCallbacks[message.data.id];
        }
    }

    private onError(err: any) {
        console.error("Web worker error", err);
    };

    public sendMessage(message: any, callback: any, wrk?: Worker) {
        const messageId = this.getSequenceNumber();
        message.id = messageId;
        if (wrk) {
            wrk.postMessage(message);
        } else {
            this.worker?.postMessage(message);
        }
        this.messagesCallbacks[messageId] = callback;
    }

    /*
        The code above this line is a generic implementation of a web worker client.
        It could be refactored and moved to its own file.
        Code below are specific methods for this particular worker.
    */

    /**
     * Get the tile from the tile index
     * @param x
     * @param y
     * @param z
     * @param layerIndex
     * @param callback
     */
    //var queue = {};
    //var queueSize = 0;


    /**
     * Get the features that intersect with the specified polygon
     * @param geom
     */
    public intersects(geom: Geometry) {
        const message = {
            cmd: "intersects",
            data: {feature: geom}
        };
        return new Promise<Feature[]>((resolve, _reject) => {
            this.sendMessage(message, resolve);
        });
    }

    /**
     * Get the features that intersect with the specified polygon
     * @param geom
     * @param territoryId
     * @param callback
     */
    public contains(geom: any, territoryId: number, callback: any) {
        const message = {
            cmd: "contains",
            data: {feature: geom, territoryId: territoryId}
        };
        this.sendMessage(message, callback);
    }

     /**
     * Get the features that interact with the specified feature in any way (intersects, contains, within)
     * @param geom
     * @param callback
     */
    public anyInteract(geom: any, callback: any) {
        const message = {
            cmd: "anyInteract",
            data: {feature: geom}
        };
        this.sendMessage(message, callback);
    }

    /**
     * Get the features that are within with the specified polygon.
     * Note: should be used for point layers.
     * @param geom
     * @param territoryId
     * @param callback
     */
    public within(geom: any, territoryId: number, callback: any) {
        const message = {
            cmd: "within",
            data: {feature: geom, territoryId: territoryId}
        };
        this.sendMessage(message, callback);
    }

    public getFeatureById(featureId: number) {
        const message = {
            cmd: "getFeatureById",
            data: featureId
        };
        return new Promise<Feature>((resolve, _reject) => {
            this.sendMessage(message, resolve);
        });
    }

    public getFeaturesByIds(featuresIds: number[]) {
        const message = {
            cmd: "getFeaturesByIds",
            data: featuresIds
        };

        return new Promise<Feature[]>((resolve, _reject) => {
            this.sendMessage(message, resolve);
        });
    }

    /**
     * Get a feature or a list of feature matching to the given property and value.
     * @param property The member of the "properties" GeoJson property to search for
     * @param value Value can be a single value or a array
     */
    public getFeaturesByProperty(property: string, value: any) {
        const message = {
            cmd: "getFeaturesByProperty",
            data: {property: property, value: value}
        };
        return new Promise<Feature[]>((resolve, _reject) => {
            this.sendMessage(message, resolve);
        });
    }

    public getFeatureCenterByIds(featuresIds: number[], callback: any) {
        const message = {
            cmd: "getFeatureCenterByIds",
            data: featuresIds
        };
        this.sendMessage(message, callback);
    };

    public territoryVisualCenter(territoryId: number, baseFeatureIds: number[], clip: any, callback: any){

        this.getFeatureCenterByIds(baseFeatureIds, (featureCenters: any) => {
            const data: any = {};
            data[territoryId] = featureCenters;
            const message = {cmd: "territoryVisualCenters", clip, featuresByTerritory: data};
            this.sendMessage(message, callback, this.visualCenterWorker);
        });
    }

    public territoryVisualCenters(baseFeatureIdsByTerritory: any, clip: any, callback: any){
        const data: any = {}
        const dfds: any[] = []
        baseFeatureIdsByTerritory.forEach((featureIds: number[], territoryId: number) => {
            let dfd = $.Deferred();
            this.getFeaturesByIds(featureIds).then((features: Feature[]) => {
                data[territoryId] = features;
                dfd.resolve();
            });
            dfds.push(dfd);
        });

        Promise.all(dfds).then(() => {
            const message = {cmd: "territoryVisualCenters", clip, featuresByTerritory: data};
            this.sendMessage(message, callback, this.visualCenterWorker);
        });
    }

    public territoryBBox(baseFeatureIds: number[], callback: any){
        const message = {
            cmd: "territoryBBox",
            data: {baseFeatureIds: baseFeatureIds}
        };
        this.sendMessage(message, callback);
    }

    public revalidateTerritoryCache(territory: any, callback: any) {
        const message = {
            cmd: "revalidateTerritoryCache",
            data: territory
        };
        this.sendMessage(message, callback);
    }

    public territoryConvexHull(baseFeatureIds: number[], isUseCenters: boolean, callback: any){
        const message = {
            cmd: "territoryConvexHull",
            data: {baseFeatureIds: baseFeatureIds, isUseCenters: isUseCenters}
        };
        this.sendMessage(message, callback);
    }

    public territoryConcaveHull(baseFeatureIds: number, _isUseCenters: boolean, callback: any){
        const message = {
            cmd: "territoryConcaveHull",
            data: {baseFeatureIds: baseFeatureIds, isUseCenters: false}
        };
        this.sendMessage(message, callback);
    }

    public reloadLocal(outlines: any, callback: any) {
        const message = {
            cmd: "reloadLocal",
            data: outlines
        };
        this.sendMessage(message, callback);
    }


    public getAll() {
        const message = {
            cmd: "getAll"
        };
        return new Promise<Feature[]>((resolve, _reject) => {
            this.sendMessage(message, resolve);
        });
    }

    public getLean(requiredAttributes: string[], start: number, length: number): Promise<any[]> {
        const message = {
            cmd: "getLean",
            data: {requiredAttributes, start, length}
        };
        return new Promise<any[]>((resolve) => {
            this.sendMessage(message, (data: any[]) => {
                resolve(data);
            });
        });
    }
}
